import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import LiveService from "../../services/live.service";
import { useAuthStore } from "../../store/zustand/authStore";
import { CartContext } from "../../store/context/CartContext";
import { createSocketConnection } from "../../services/socket.service";

const MAX_VISIBLE_COMMENTS = 200;
const COMMENT_BATCH_INTERVAL_MS = 300;

const parseSocketPayload = (payload) => {
  if (!payload) return null;

  const rawComment =
    typeof payload === "object" && payload !== null && "data" in payload
      ? payload.data
      : payload;

  if (typeof rawComment === "string") {
    try {
      return JSON.parse(rawComment);
    } catch {
      return { message: rawComment };
    }
  }

  return rawComment;
};

const parseOnlineUser = (user) => {
  try {
    const parsedUser = typeof user === "string" ? JSON.parse(user) : user;

    if (!parsedUser?.id) return null;

    const { name, lastname, id } = parsedUser;
    return { name, lastname, id };
  } catch {
    return null;
  }
};

const useBroadcastLive = () => {
  const parentRef = useRef(null);
  const socketRef = useRef(null);
  const commentsQueueRef = useRef([]);
  const flushTimerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const [comments, setComments] = useState([]);
  const [loader, setLoader] = useState(false);
  const [isSend, setIsSend] = useState(false);
  const [onlineUsers, setOnlineUser] = useState([]);
  const [hideComments, setHideComments] = useState(false);
  const [usersDrawerOpen, setUsersDrawerOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const { setTitleSeccion } = useContext(CartContext);

  useEffect(() => {
    setTitleSeccion("Comentarios Live");
  }, [setTitleSeccion]);

  useEffect(() => {
    if (!token) return undefined;

    const socketInstance = createSocketConnection(token, "/live");
    socketRef.current = socketInstance;

    flushTimerRef.current = window.setInterval(() => {
      if (commentsQueueRef.current.length === 0) return;

      const queuedComments = commentsQueueRef.current.splice(
        0,
        commentsQueueRef.current.length
      );

      setComments((prevComments) =>
        [...prevComments, ...queuedComments].slice(-MAX_VISIBLE_COMMENTS)
      );
    }, COMMENT_BATCH_INTERVAL_MS);

    socketInstance.on("live", (data) => {
      const parsedComment = parseSocketPayload(data);

      if (!parsedComment) return;

      commentsQueueRef.current.push(parsedComment);
    });

    socketInstance.on("getOnlineUsers", (data) => {
      const parsedUsers = (data?.onlineUsers ?? [])
        .map(parseOnlineUser)
        .filter(Boolean);

      const uniqueUsers = parsedUsers.filter(
        (user, index, self) => index === self.findIndex((u) => u.id === user.id)
      );
      setOnlineUser(uniqueUsers);
    });

    socketInstance.on("getLiveStatus", (data) => {
      if (data?.liveStatus === "ON") {
        setHideComments(false);
      } else {
        setHideComments(true);
      }
    });

    return () => {
      if (flushTimerRef.current) {
        window.clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }

      commentsQueueRef.current = [];
      socketInstance.off("live");
      socketInstance.off("getOnlineUsers");
      socketInstance.off("getLiveStatus");
      socketInstance.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 50;
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  useLayoutEffect(() => {
    if (parentRef.current && isAtBottomRef.current) {
      parentRef.current.scrollTop = parentRef.current.scrollHeight;
    }
  }, [comments]);

  const sendNotification = async () => {
    setLoader(true);
    try {
      const response = await LiveService.sendStartLiveNotification();
      if (response) {
        setIsSend(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const manageLiveChat = async () => {
    const socket = socketRef.current;

    if (!socket?.connected) return;

    try {
      socket.emit("manageSocket", {
        action: !hideComments ? "PAUSE" : "RESUME",
      });
    } catch (e) {
      console.log(e);
    }
  };

  return {
    parentRef,
    comments,
    loader,
    isSend,
    setIsSend,
    onlineUsers,
    hideComments,
    usersDrawerOpen,
    setUsersDrawerOpen,
    user,
    sendNotification,
    manageLiveChat,
  };
};

export default useBroadcastLive;
